// src/stores/credentialsStore.js
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import config from "../config";
import logger from "../utils/logger";

/**
 * Credentials Store - Zustand-based credential state management
 *
 * Manages:
 * - Credentials list for selected device
 * - Patrons metadata map (patronId -> patron data)
 * - Pending operations (additions, deletions, full deletions)
 * - Offline device credentials tracking
 * - Credentials loaded state
 * - Credential status overrides (local storage based)
 *
 * Features:
 * - Fetch credentials from backend (with device metadata enrichment)
 * - Add/remove credentials to/from devices
 * - Delete credentials completely (from all devices)
 * - Track pending operations with optimistic updates
 * - Patron metadata fetching and caching
 * - Status persistence in localStorage
 *
 * Migration Note:
 * This store replaces credential-related state from AppContent.js
 * See docs/ZUSTAND_MIGRATION_GUIDE.md for migration details
 */

const useCredentialsStore = create(
  devtools(
    (set, get) => ({
      // --- State ---
      credentials: [],
      credentialsLoaded: false,
      patronsMap: {}, // Map of patronId -> patron data
      pendingDeletionCredentials: new Set(), // Set of "deviceId:credentialId" composite keys
      pendingAdditionCredentials: new Set(), // Set of "deviceId:credentialId" composite keys
      pendingDeletionPatrons: new Set(), // Set of patronIds pending deletion
      pendingFullCredentialDeletions: new Set(), // Set of credentialIds being fully deleted (from all devices)
      devicesWithPendingDeletions: new Set(), // Set of deviceIds that have any credential deletion in progress
      offlineDeviceCredentials: new Set(), // Set of "deviceId:credentialId" composite keys for timed out deletions
      lastCredentialDownload: null, // Track credentialDownloaded events
      lastCredentialProvisioned: null, // Track credentialProvisioned events
      lastCredentialRemoved: null, // Track credentialRemovedFromDevice events

      // --- Local Storage Helpers ---

      /**
       * Get credential status overrides from localStorage
       * @returns {Object} Map of credentialId -> status
       */
      getStatusStore: () => {
        try {
          const statuses = localStorage.getItem("credentialStatuses");
          return statuses ? JSON.parse(statuses) : {};
        } catch (e) {
          return {};
        }
      },

      /**
       * Set credential status in localStorage
       * @param {string} credentialId - Credential ID
       * @param {string} status - Status value
       */
      setStatusInStore: (credentialId, status) => {
        try {
          const statuses = get().getStatusStore();
          statuses[credentialId] = status;
          localStorage.setItem("credentialStatuses", JSON.stringify(statuses));
        } catch (e) {
          logger.error("Failed to set status in store:", e);
        }
      },

      /**
       * Remove credential status from localStorage
       * @param {string} credentialId - Credential ID
       */
      removeStatusFromStore: (credentialId) => {
        try {
          const statuses = get().getStatusStore();
          delete statuses[credentialId];
          localStorage.setItem("credentialStatuses", JSON.stringify(statuses));
        } catch (e) {
          logger.error("Failed to remove status from store:", e);
        }
      },

      // --- Setters ---

      /**
       * Set credentials list
       * @param {Array} credentials - Array of credential objects
       */
      /**
       * Set credentials (supports callback or direct array)
       * @param {Array|Function} value - New credentials array or callback (prev => newArray)
       */
      setCredentials: (value) => {
        if (typeof value === 'function') {
          set((state) => ({ credentials: value(state.credentials) }), false, 'setCredentials');
        } else {
          set({ credentials: value }, false, 'setCredentials');
        }
      },

      /**
       * Set credentials loaded state
       * @param {boolean} loaded - Loaded status
       */
      setCredentialsLoaded: (loaded) => {
        set({ credentialsLoaded: loaded }, false, "setCredentialsLoaded");
      },

      /**
       * Set patrons map
       * @param {Object} patronsMap - Map of patronId -> patron data
       */
      /**
       * Set patrons map (supports callback or direct object)
       * @param {Object|Function} value - New map or callback (prev => newMap)
       */
      setPatronsMap: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({ patronsMap: value(state.patronsMap) }),
            false,
            "setPatronsMap"
          );
        } else {
          set({ patronsMap: value }, false, "setPatronsMap");
        }
      },

      /**
       * Add patron to map
       * @param {string} patronId - Patron ID
       * @param {Object} patron - Patron data
       */
      addPatron: (patronId, patron) => {
        set(
          (state) => ({
            patronsMap: { ...state.patronsMap, [patronId]: patron },
          }),
          false,
          "addPatron"
        );
      },

      /**
       * Set last credential download event
       * @param {Object} event - Credential download event data
       */
      setLastCredentialDownload: (event) => {
        set(
          { lastCredentialDownload: event },
          false,
          "setLastCredentialDownload"
        );
      },

      /**
       * Set last credential provisioned event
       * @param {Object} event - Credential provisioned event data
       */
      setLastCredentialProvisioned: (event) => {
        set(
          { lastCredentialProvisioned: event },
          false,
          "setLastCredentialProvisioned"
        );
      },

      /**
       * Set last credential removed event
       * @param {Object} event - Credential removed event data
       */
      setLastCredentialRemoved: (event) => {
        set(
          { lastCredentialRemoved: event },
          false,
          "setLastCredentialRemoved"
        );
      },

      // --- Pending Operations Management ---

      /**
       * Set pending deletion credentials (supports callback or direct Set)
       * @param {Set|Function} value - New Set or callback (prev => newSet)
       */
      setPendingDeletionCredentials: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({
              pendingDeletionCredentials: value(
                state.pendingDeletionCredentials
              ),
            }),
            false,
            "setPendingDeletionCredentials"
          );
        } else {
          set(
            { pendingDeletionCredentials: value },
            false,
            "setPendingDeletionCredentials"
          );
        }
      },

      /**
       * Set pending addition credentials (supports callback or direct Set)
       * @param {Set|Function} value - New Set or callback (prev => newSet)
       */
      setPendingAdditionCredentials: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({
              pendingAdditionCredentials: value(
                state.pendingAdditionCredentials
              ),
            }),
            false,
            "setPendingAdditionCredentials"
          );
        } else {
          set(
            { pendingAdditionCredentials: value },
            false,
            "setPendingAdditionCredentials"
          );
        }
      },

      /**
       * Set pending deletion patrons (supports callback or direct Set)
       * @param {Set|Function} value - New Set or callback (prev => newSet)
       */
      setPendingDeletionPatrons: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({
              pendingDeletionPatrons: value(state.pendingDeletionPatrons),
            }),
            false,
            "setPendingDeletionPatrons"
          );
        } else {
          set(
            { pendingDeletionPatrons: value },
            false,
            "setPendingDeletionPatrons"
          );
        }
      },

      /**
       * Set pending full credential deletions (supports callback or direct Set)
       * @param {Set|Function} value - New Set or callback (prev => newSet)
       */
      setPendingFullCredentialDeletions: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({
              pendingFullCredentialDeletions: value(
                state.pendingFullCredentialDeletions
              ),
            }),
            false,
            "setPendingFullCredentialDeletions"
          );
        } else {
          set(
            { pendingFullCredentialDeletions: value },
            false,
            "setPendingFullCredentialDeletions"
          );
        }
      },

      /**
       * Set devices with pending deletions (supports callback or direct Set)
       * @param {Set|Function} value - New Set or callback (prev => newSet)
       */
      setDevicesWithPendingDeletions: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({
              devicesWithPendingDeletions: value(
                state.devicesWithPendingDeletions
              ),
            }),
            false,
            "setDevicesWithPendingDeletions"
          );
        } else {
          set(
            { devicesWithPendingDeletions: value },
            false,
            "setDevicesWithPendingDeletions"
          );
        }
      },

      /**
       * Set offline device credentials (supports callback or direct Set)
       * @param {Set|Function} value - New Set or callback (prev => newSet)
       */
      setOfflineDeviceCredentials: (value) => {
        if (typeof value === "function") {
          set(
            (state) => ({
              offlineDeviceCredentials: value(state.offlineDeviceCredentials),
            }),
            false,
            "setOfflineDeviceCredentials"
          );
        } else {
          set(
            { offlineDeviceCredentials: value },
            false,
            "setOfflineDeviceCredentials"
          );
        }
      },

      /**
       * Add pending deletion credential
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       */
      addPendingDeletion: (deviceId, credentialId) => {
        const pendingKey = `${deviceId}:${credentialId}`;
        set(
          (state) => ({
            pendingDeletionCredentials: new Set([
              ...state.pendingDeletionCredentials,
              pendingKey,
            ]),
            devicesWithPendingDeletions: new Set([
              ...state.devicesWithPendingDeletions,
              deviceId,
            ]),
          }),
          false,
          "addPendingDeletion"
        );
      },

      /**
       * Remove pending deletion credential
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       */
      removePendingDeletion: (deviceId, credentialId) => {
        const pendingKey = `${deviceId}:${credentialId}`;
        set(
          (state) => {
            const newPendingDeletions = new Set(
              state.pendingDeletionCredentials
            );
            newPendingDeletions.delete(pendingKey);

            // Check if device has any other pending deletions
            const hasOtherPendingDeletions = Array.from(
              newPendingDeletions
            ).some((key) => key.startsWith(`${deviceId}:`));

            const newDevicesWithPendingDeletions = new Set(
              state.devicesWithPendingDeletions
            );
            if (!hasOtherPendingDeletions) {
              newDevicesWithPendingDeletions.delete(deviceId);
            }

            return {
              pendingDeletionCredentials: newPendingDeletions,
              devicesWithPendingDeletions: newDevicesWithPendingDeletions,
            };
          },
          false,
          "removePendingDeletion"
        );
      },

      /**
       * Add pending addition credential
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       */
      addPendingAddition: (deviceId, credentialId) => {
        const pendingKey = `${deviceId}:${credentialId}`;
        set(
          (state) => ({
            pendingAdditionCredentials: new Set([
              ...state.pendingAdditionCredentials,
              pendingKey,
            ]),
          }),
          false,
          "addPendingAddition"
        );
      },

      /**
       * Remove pending addition credential
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       */
      removePendingAddition: (deviceId, credentialId) => {
        const pendingKey = `${deviceId}:${credentialId}`;
        set(
          (state) => {
            const newPendingAdditions = new Set(
              state.pendingAdditionCredentials
            );
            newPendingAdditions.delete(pendingKey);
            return { pendingAdditionCredentials: newPendingAdditions };
          },
          false,
          "removePendingAddition"
        );
      },

      /**
       * Add pending full credential deletion
       * @param {string} credentialId - Credential ID
       */
      addPendingFullDeletion: (credentialId) => {
        set(
          (state) => ({
            pendingFullCredentialDeletions: new Set([
              ...state.pendingFullCredentialDeletions,
              credentialId,
            ]),
          }),
          false,
          "addPendingFullDeletion"
        );
      },

      /**
       * Remove pending full credential deletion
       * @param {string} credentialId - Credential ID
       */
      removePendingFullDeletion: (credentialId) => {
        set(
          (state) => {
            const newPendingFullDeletions = new Set(
              state.pendingFullCredentialDeletions
            );
            newPendingFullDeletions.delete(credentialId);
            return { pendingFullCredentialDeletions: newPendingFullDeletions };
          },
          false,
          "removePendingFullDeletion"
        );
      },

      /**
       * Add pending patron deletion
       * @param {string} patronId - Patron ID
       */
      addPendingPatronDeletion: (patronId) => {
        set(
          (state) => ({
            pendingDeletionPatrons: new Set([
              ...state.pendingDeletionPatrons,
              patronId,
            ]),
          }),
          false,
          "addPendingPatronDeletion"
        );
      },

      /**
       * Remove pending patron deletion
       * @param {string} patronId - Patron ID
       */
      removePendingPatronDeletion: (patronId) => {
        set(
          (state) => {
            const newPendingPatronDeletions = new Set(
              state.pendingDeletionPatrons
            );
            newPendingPatronDeletions.delete(patronId);
            return { pendingDeletionPatrons: newPendingPatronDeletions };
          },
          false,
          "removePendingPatronDeletion"
        );
      },

      /**
       * Add offline device credential
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       */
      addOfflineDeviceCredential: (deviceId, credentialId) => {
        const offlineKey = `${deviceId}:${credentialId}`;
        set(
          (state) => ({
            offlineDeviceCredentials: new Set([
              ...state.offlineDeviceCredentials,
              offlineKey,
            ]),
          }),
          false,
          "addOfflineDeviceCredential"
        );
      },

      /**
       * Remove offline device credential
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       */
      removeOfflineDeviceCredential: (deviceId, credentialId) => {
        const offlineKey = `${deviceId}:${credentialId}`;
        set(
          (state) => {
            const newOfflineCredentials = new Set(
              state.offlineDeviceCredentials
            );
            newOfflineCredentials.delete(offlineKey);
            return { offlineDeviceCredentials: newOfflineCredentials };
          },
          false,
          "removeOfflineDeviceCredential"
        );
      },

      // --- API Operations ---

      /**
       * Fetch credentials for a device
       * @param {string} deviceId - Device ID
       * @param {Function} authenticatedFetch - Authenticated fetch function
       * @returns {Promise<Array>} Credentials array
       */
      fetchCredentials: async (deviceId, authenticatedFetch) => {
        if (!deviceId) {
          set(
            { credentials: [], credentialsLoaded: true },
            false,
            "fetchCredentials:empty"
          );
          return [];
        }

        try {
          logger.log(`Fetching credentials for device ${deviceId}...`);
          const response = await authenticatedFetch(
            `${config.apiEndpoint}/get-credentials?deviceId=${deviceId}`,
            { headers: { "x-api-key": config.apiKey } }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch credentials: ${response.status}`);
          }

          const data = await response.json();
          const credentials = data.credentials || [];

          // Merge with status store
          const statusStore = get().getStatusStore();
          const mergedCredentials = credentials.map((cred) => ({
            ...cred,
            status: cred.status || statusStore[cred.id] || "Active",
          }));

          set(
            {
              credentials: mergedCredentials,
              credentialsLoaded: true,
            },
            false,
            "fetchCredentials:success"
          );

          logger.log(`Fetched ${mergedCredentials.length} credentials`);
          return mergedCredentials;
        } catch (error) {
          logger.error("Failed to fetch credentials:", error);
          set(
            {
              credentials: [],
              credentialsLoaded: true,
            },
            false,
            "fetchCredentials:error"
          );
          throw error;
        }
      },

      /**
       * Fetch patron metadata
       * @param {string} patronId - Patron ID
       * @param {Function} authenticatedFetch - Authenticated fetch function
       * @returns {Promise<Object|null>} Patron data or null
       */
      fetchPatron: async (patronId, authenticatedFetch) => {
        const { patronsMap, addPatron } = get();

        // Check if already cached
        if (patronsMap[patronId]) {
          return patronsMap[patronId];
        }

        try {
          logger.log(`Fetching patron metadata for ${patronId}...`);
          const response = await authenticatedFetch(
            `${config.apiEndpoint}/patron-metadata?patronId=${patronId}`,
            { headers: { "x-api-key": config.apiKey } }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch patron: ${response.status}`);
          }

          const patron = await response.json();
          addPatron(patronId, patron);

          return patron;
        } catch (error) {
          logger.error(`Failed to fetch patron ${patronId}:`, error);
          return null;
        }
      },

      /**
       * Fetch multiple patrons in parallel
       * @param {Array<string>} patronIds - Array of patron IDs
       * @param {Function} authenticatedFetch - Authenticated fetch function
       * @returns {Promise<void>}
       */
      fetchPatrons: async (patronIds, authenticatedFetch) => {
        const { patronsMap } = get();

        // Filter out already fetched patrons
        const unfetchedPatronIds = patronIds.filter(
          (id) => id && !patronsMap[id]
        );

        if (unfetchedPatronIds.length === 0) {
          return;
        }

        logger.log(
          `Fetching ${unfetchedPatronIds.length} patron metadata records...`
        );

        const fetchPromises = unfetchedPatronIds.map((patronId) =>
          get().fetchPatron(patronId, authenticatedFetch)
        );

        await Promise.all(fetchPromises);
      },

      /**
       * Add credential to device
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       * @param {Function} authenticatedFetch - Authenticated fetch function
       * @returns {Promise<Object>} Response data
       */
      addCredentialToDevice: async (
        deviceId,
        credentialId,
        authenticatedFetch
      ) => {
        logger.log(
          `Adding credential ${credentialId} to device ${deviceId}...`
        );

        try {
          // Add to pending additions
          get().addPendingAddition(deviceId, credentialId);

          const response = await authenticatedFetch(
            `${config.apiEndpoint}/credentials/${credentialId}/devices/${deviceId}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": config.apiKey,
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.error || `Failed to add credential: ${response.status}`
            );
          }

          const data = await response.json();
          logger.log(
            `Credential ${credentialId} added to device ${deviceId} successfully`
          );

          return data;
        } catch (error) {
          // Remove from pending additions on error
          get().removePendingAddition(deviceId, credentialId);
          logger.error("Failed to add credential to device:", error);
          throw error;
        }
      },

      /**
       * Remove credential from device
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       * @param {Function} authenticatedFetch - Authenticated fetch function
       * @returns {Promise<Object>} Response data
       */
      removeCredentialFromDevice: async (
        deviceId,
        credentialId,
        authenticatedFetch
      ) => {
        logger.log(
          `Removing credential ${credentialId} from device ${deviceId}...`
        );

        try {
          // Add to pending deletions
          get().addPendingDeletion(deviceId, credentialId);

          const response = await authenticatedFetch(
            `${config.apiEndpoint}/credentials/${credentialId}/devices/${deviceId}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": config.apiKey,
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.error ||
                `Failed to remove credential: ${response.status}`
            );
          }

          const data = await response.json();
          logger.log(
            `Credential ${credentialId} removal initiated from device ${deviceId}`
          );

          return data;
        } catch (error) {
          // Remove from pending deletions on error
          get().removePendingDeletion(deviceId, credentialId);
          logger.error("Failed to remove credential from device:", error);
          throw error;
        }
      },

      /**
       * Delete credential completely (from all devices)
       * @param {string} credentialId - Credential ID
       * @param {Function} authenticatedFetch - Authenticated fetch function
       * @returns {Promise<Object>} Response data
       */
      deleteCredential: async (credentialId, authenticatedFetch) => {
        logger.log(`Deleting credential ${credentialId} completely...`);

        try {
          // Add to pending full deletions
          get().addPendingFullDeletion(credentialId);

          const response = await authenticatedFetch(
            `${config.apiEndpoint}/credentials/${credentialId}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": config.apiKey,
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.error ||
                `Failed to delete credential: ${response.status}`
            );
          }

          const data = await response.json();
          logger.log(`Credential ${credentialId} deletion initiated`);

          return data;
        } catch (error) {
          // Remove from pending full deletions on error
          get().removePendingFullDeletion(credentialId);
          logger.error("Failed to delete credential:", error);
          throw error;
        }
      },

      // --- Helper Methods ---

      /**
       * Check if credential is pending deletion
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       * @returns {boolean} True if pending deletion
       */
      isPendingDeletion: (deviceId, credentialId) => {
        const { pendingDeletionCredentials } = get();
        const pendingKey = `${deviceId}:${credentialId}`;
        return pendingDeletionCredentials.has(pendingKey);
      },

      /**
       * Check if credential is pending addition
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       * @returns {boolean} True if pending addition
       */
      isPendingAddition: (deviceId, credentialId) => {
        const { pendingAdditionCredentials } = get();
        const pendingKey = `${deviceId}:${credentialId}`;
        return pendingAdditionCredentials.has(pendingKey);
      },

      /**
       * Check if credential is pending full deletion
       * @param {string} credentialId - Credential ID
       * @returns {boolean} True if pending full deletion
       */
      isPendingFullDeletion: (credentialId) => {
        const { pendingFullCredentialDeletions } = get();
        return pendingFullCredentialDeletions.has(credentialId);
      },

      /**
       * Check if patron is pending deletion
       * @param {string} patronId - Patron ID
       * @returns {boolean} True if pending deletion
       */
      isPendingPatronDeletion: (patronId) => {
        const { pendingDeletionPatrons } = get();
        return pendingDeletionPatrons.has(patronId);
      },

      /**
       * Check if device/credential is offline
       * @param {string} deviceId - Device ID
       * @param {string} credentialId - Credential ID
       * @returns {boolean} True if offline
       */
      isOfflineDeviceCredential: (deviceId, credentialId) => {
        const { offlineDeviceCredentials } = get();
        const offlineKey = `${deviceId}:${credentialId}`;
        return offlineDeviceCredentials.has(offlineKey);
      },

      /**
       * Clear all credentials state
       */
      clearCredentials: () => {
        set(
          {
            credentials: [],
            credentialsLoaded: false,
            pendingDeletionCredentials: new Set(),
            pendingAdditionCredentials: new Set(),
            pendingFullCredentialDeletions: new Set(),
            devicesWithPendingDeletions: new Set(),
            offlineDeviceCredentials: new Set(),
          },
          false,
          "clearCredentials"
        );
      },

      /**
       * Clear all patron data
       */
      clearPatrons: () => {
        set(
          {
            patronsMap: {},
            pendingDeletionPatrons: new Set(),
          },
          false,
          "clearPatrons"
        );
      },

      /**
       * Clear all state
       */
      clearAll: () => {
        get().clearCredentials();
        get().clearPatrons();
        set(
          {
            lastCredentialDownload: null,
            lastCredentialProvisioned: null,
            lastCredentialRemoved: null,
          },
          false,
          "clearAll"
        );
      },
    }),
    { name: "CredentialsStore" }
  )
);

export default useCredentialsStore;
